import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSsgTv } from '../../api';
import { SSG_COMPETITION_NAME } from '../../ssgConstants';

const RANK_TINT = { 1: '#f59e0b', 2: '#94a3b8', 3: '#b45309' };
const ACCENT = '#3b82f6';

// "Compute row height and font size from squad size so everyone fits without scrolling" — the
// standings panel splits the full squad into 2 side-by-side columns; row height is whatever the
// available vertical space divides into evenly for the taller column, font size scales with it,
// clamped to the kiosk's own readability floor (26px, read from 5 metres) and a sane ceiling so a
// tiny squad doesn't get comically large rows.
function computeStandingsMetrics(totalPlayers, availableHeight) {
  const perColumn = Math.max(1, Math.ceil(totalPlayers / 2));
  const idealRowHeight = availableHeight / perColumn;
  const rowHeight = Math.max(30, Math.min(58, Math.floor(idealRowHeight)));
  const fontSize = Math.max(26, Math.min(34, Math.floor(rowHeight * 0.5)));
  return { rowHeight, fontSize, perColumn };
}

function Avatar({ name, photo, size }) {
  if (photo) return <img src={photo} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: size * 0.36, color: '#fff',
    }}>{initials}</div>
  );
}

function RightCard({ title, children, style }) {
  return (
    <div style={{
      flex: 1, minHeight: 0, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 14, padding: '14px 20px', display: 'flex', flexDirection: 'column', ...style,
    }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{title}</div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>{children}</div>
    </div>
  );
}

// Fixed-width stat columns kept deliberately tight — at a 2-sub-column split of a 7/12-width
// panel on a 1920px kiosk, anything wider here starves the flexible name column down to nothing
// (confirmed by hand: names rendered as a single truncated letter before this was tightened).
const STANDINGS_COLS = '54px 1fr 26px 26px 26px 26px 42px 46px 48px';

function StandingsHeader({ fontSize }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: STANDINGS_COLS, fontSize: Math.max(13, fontSize * 0.5),
      fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em',
      paddingLeft: 6, paddingBottom: 6, borderBottom: '2px solid rgba(255,255,255,0.15)',
    }}>
      <span>#</span><span>Player</span><span style={{ textAlign: 'center' }}>P</span><span style={{ textAlign: 'center' }}>W</span>
      <span style={{ textAlign: 'center' }}>D</span><span style={{ textAlign: 'center' }}>L</span><span style={{ textAlign: 'center' }}>Bon</span>
      <span style={{ textAlign: 'center' }}>Pts</span><span style={{ textAlign: 'center' }}>+/-</span>
    </div>
  );
}

function StandingsColumn({ players, rowHeight, fontSize, pulseActive, changedArrowIds }) {
  return (
    <div>
      <StandingsHeader fontSize={fontSize} />
      {players.map(p => {
        const tint = p.rank <= 3 && !p.shared_rank ? RANK_TINT[p.rank] : null;
        return (
          <div
            key={p.player_id}
            className={pulseActive && p.rank_delta !== 0 ? 'ssg-tv-pulse' : ''}
            style={{
              display: 'grid', gridTemplateColumns: STANDINGS_COLS,
              alignItems: 'center', height: rowHeight, fontSize, lineHeight: 1,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              background: tint ? tint + '22' : 'transparent',
              paddingLeft: 6,
            }}
          >
            <span style={{ fontWeight: 900, color: tint || '#fff', paddingRight: 6 }}>{p.shared_rank ? '=' : ''}{p.rank}</span>
            <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 6, minWidth: 0 }}>{p.name}</span>
            <span style={{ color: '#94a3b8', textAlign: 'center' }}>{p.played}</span>
            <span style={{ color: '#94a3b8', textAlign: 'center' }}>{p.won}</span>
            <span style={{ color: '#94a3b8', textAlign: 'center' }}>{p.drawn}</span>
            <span style={{ color: '#94a3b8', textAlign: 'center' }}>{p.lost}</span>
            <span style={{ color: '#94a3b8', textAlign: 'center' }}>{p.bonus_points}</span>
            <span style={{ fontWeight: 900, textAlign: 'center' }}>{p.total_points}</span>
            <span
              className={changedArrowIds.has(p.player_id) ? 'ssg-tv-arrow-in' : ''}
              style={{ textAlign: 'center', fontWeight: 900, color: p.rank_delta > 0 ? '#10b981' : p.rank_delta < 0 ? '#ef4444' : '#475569' }}
            >
              {p.rank_delta > 0 ? `▲${p.rank_delta}` : p.rank_delta < 0 ? `▼${Math.abs(p.rank_delta)}` : '–'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function formatCountdown(countdown) {
  const parts = [];
  if (countdown?.sessions_left != null) parts.push(`${countdown.sessions_left} sessions`);
  if (countdown?.days_left != null) parts.push(`${countdown.days_left} days left`);
  return parts.length ? parts.join(' · ') : 'Season in progress';
}

export default function SsgTv() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const debug = searchParams.get('debug') === '1';

  const [payload, setPayload] = useState(null);
  const [lastPollAt, setLastPollAt] = useState(null);
  const [offline, setOffline] = useState(false);
  const [pulseActive, setPulseActive] = useState(false);
  const [changedArrowIds, setChangedArrowIds] = useState(new Set());
  const prevRankDeltaRef = useRef({});
  const hasLoadedOnceRef = useRef(false);
  const standingsRef = useRef(null);
  const [standingsHeight, setStandingsHeight] = useState(700);

  const poll = useCallback(() => {
    if (!token) return;
    getSsgTv(token).then(data => {
      setOffline(false);
      setLastPollAt(new Date());

      if (hasLoadedOnceRef.current) {
        const changed = new Set();
        (data?.leaderboard || []).forEach(p => {
          const prev = prevRankDeltaRef.current[p.player_id];
          if (prev !== undefined && prev !== p.rank_delta) changed.add(p.player_id);
        });
        if (changed.size) {
          setChangedArrowIds(changed);
          setTimeout(() => setChangedArrowIds(new Set()), 1300);
        }
      } else if (data?.last_session) {
        // "Rows whose rank changed pulse once on load" — only on this page's very first render,
        // never re-triggered by later polls even if last_session is still within its 48h window.
        setPulseActive(true);
        setTimeout(() => setPulseActive(false), 1800);
      }
      hasLoadedOnceRef.current = true;

      const nextDeltas = {};
      (data?.leaderboard || []).forEach(p => { nextDeltas[p.player_id] = p.rank_delta; });
      prevRankDeltaRef.current = nextDeltas;

      setPayload(data);
    }).catch(() => setOffline(true));
  }, [token]);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 60000);
    return () => clearInterval(interval);
  }, [poll]);

  useEffect(() => {
    if (standingsRef.current) setStandingsHeight(standingsRef.current.clientHeight);
  }, [payload]);

  if (!token) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#0a0e14', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontFamily: 'Inter, sans-serif' }}>
        Missing ?token=
      </div>
    );
  }

  if (!payload) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#0a0e14', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontFamily: 'Inter, sans-serif' }}>
        {offline ? 'Unable to load — retrying…' : 'Loading…'}
      </div>
    );
  }

  const { period, leader, leaders, prize, leaderboard, movers, duel_of_the_week, form, last_session, hall_of_fame, countdown } = payload;
  const isChampionCard = period.status === 'closed';
  const championNames = (leaders?.length ? leaders : leader ? [leader] : []).map(p => p.name).join(' & ');

  const { rowHeight, fontSize, perColumn } = computeStandingsMetrics(leaderboard.length, standingsHeight - 34);
  const colA = leaderboard.slice(0, perColumn);
  const colB = leaderboard.slice(perColumn);

  return (
    <div style={{
      width: '100vw', height: '100vh', overflow: 'hidden', boxSizing: 'border-box',
      background: 'linear-gradient(160deg, #0a0e14, #0d1520 60%, #0a0e14)', color: '#fff',
      fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', padding: '28px 40px',
      position: 'relative',
    }}>
      <style>{`
        @keyframes ssgTvPulse {
          0% { background-color: rgba(59,130,246,0); }
          25% { background-color: rgba(59,130,246,0.4); }
          100% { background-color: rgba(59,130,246,0); }
        }
        .ssg-tv-pulse { animation: ssgTvPulse 1.8s ease-out; }
        @keyframes ssgTvArrowIn {
          0% { opacity: 0; transform: translateY(-6px) scale(0.6); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ssg-tv-arrow-in { display: inline-block; animation: ssgTvArrowIn 0.5s ease-out; }
        @keyframes ssgTvFooterGlow {
          0%, 100% { background-color: rgba(59,130,246,0.06); }
          50% { background-color: rgba(59,130,246,0.16); }
        }
        .ssg-tv-footer-live { animation: ssgTvFooterGlow 3s ease-in-out infinite; }
      `}</style>

      {offline && (
        <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#f59e0b', zIndex: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} /> Offline
        </div>
      )}
      {debug && (
        <div style={{ position: 'absolute', bottom: 6, right: 10, fontSize: 12, color: '#475569', zIndex: 10 }}>
          Last poll: {lastPollAt ? lastPollAt.toLocaleTimeString('en-GB') : '—'}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexShrink: 0, marginBottom: 20 }}>
        <div style={{ fontSize: 34, fontWeight: 900, letterSpacing: '-0.01em' }}>
          {SSG_COMPETITION_NAME} <span style={{ color: '#475569' }}>—</span> {period.name} <span style={{ color: '#475569' }}>—</span> MD {period.start_matchday}–{period.end_matchday}
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#94a3b8' }}>{formatCountdown(countdown)}</div>
      </div>

      {/* Body: 12-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 28, flex: 1, minHeight: 0 }}>
        {/* Left: standings, 7 cols, 2 sub-columns — each with its own header (a single shared
            header above both columns would only visually line up with the first one). */}
        <div ref={standingsRef} style={{ gridColumn: 'span 7', minHeight: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, height: '100%' }}>
            <StandingsColumn players={colA} rowHeight={rowHeight} fontSize={fontSize} pulseActive={pulseActive} changedArrowIds={changedArrowIds} />
            <StandingsColumn players={colB} rowHeight={rowHeight} fontSize={fontSize} pulseActive={pulseActive} changedArrowIds={changedArrowIds} />
          </div>
        </div>

        {/* Right: 4 stacked cards, 5 cols */}
        <div style={{ gridColumn: 'span 5', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <RightCard
            title={isChampionCard ? 'Period Champion' : 'Leader'}
            style={isChampionCard ? { border: '2px solid #f59e0b', background: 'rgba(245,158,11,0.08)' } : undefined}
          >
            {leader ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <Avatar name={leader.name} photo={leader.photo} size={84} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 30, fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isChampionCard ? '#f59e0b' : '#fff' }}>
                    {isChampionCard ? championNames : leader.name}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#94a3b8', marginTop: 2 }}>{leader.total_points} pts</div>
                  {prize && <div style={{ fontSize: 16, color: '#f59e0b', marginTop: 4 }}>🏆 {prize}</div>}
                </div>
              </div>
            ) : <div style={{ fontSize: 18, color: '#475569' }}>No standings yet.</div>}
          </RightCard>

          <RightCard title="Risers & Fallers">
            <div style={{ display: 'flex', gap: 20 }}>
              <div style={{ flex: 1 }}>
                {movers.up.length === 0 ? <div style={{ fontSize: 15, color: '#475569' }}>No risers yet</div> : movers.up.map(p => (
                  <div key={p.player_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, marginBottom: 4 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    <span style={{ color: '#10b981', fontWeight: 800, marginLeft: 8 }}>▲{p.rank_delta}</span>
                  </div>
                ))}
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.1)' }} />
              <div style={{ flex: 1 }}>
                {movers.down.length === 0 ? <div style={{ fontSize: 15, color: '#475569' }}>No fallers yet</div> : movers.down.map(p => (
                  <div key={p.player_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, marginBottom: 4 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    <span style={{ color: '#ef4444', fontWeight: 800, marginLeft: 8 }}>▼{Math.abs(p.rank_delta)}</span>
                  </div>
                ))}
              </div>
            </div>
          </RightCard>

          <RightCard title="Duel of the Week">
            {duel_of_the_week ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar name={duel_of_the_week[0].name} photo={duel_of_the_week[0].photo} size={56} />
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{duel_of_the_week[0].name}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, color: '#475569', fontWeight: 800 }}>VS</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: ACCENT }}>{Math.abs(duel_of_the_week[0].total_points - duel_of_the_week[1].total_points)} pts</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Avatar name={duel_of_the_week[1].name} photo={duel_of_the_week[1].photo} size={56} />
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{duel_of_the_week[1].name}</div>
                </div>
              </div>
            ) : <div style={{ fontSize: 18, color: '#475569' }}>Not enough data yet.</div>}
          </RightCard>

          <RightCard title="Form">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 17 }}>
                Longest unbeaten: {form.longest_unbeaten ? <><b>{form.longest_unbeaten.name}</b> — {form.longest_unbeaten.run}</> : <span style={{ color: '#475569' }}>—</span>}
              </div>
              <div style={{ fontSize: 17 }}>
                Last MVP: {form.last_mvp ? <b>{form.last_mvp.name}</b> : <span style={{ color: '#475569' }}>—</span>}
              </div>
            </div>
          </RightCard>
        </div>
      </div>

      {/* Footer */}
      <div className={last_session ? 'ssg-tv-footer-live' : ''} style={{
        flexShrink: 0, marginTop: 20, padding: '10px 20px', borderRadius: 12,
        background: last_session ? undefined : 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 17,
      }}>
        <div>
          {last_session ? (
            <>
              <b>{new Date(last_session.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</b>
              {last_session.mvp && <> · MVP: <b>{last_session.mvp.name}</b></>}
              {last_session.biggest_bonus && <> · Biggest bonus: <b>{last_session.biggest_bonus.name}</b> +{last_session.biggest_bonus.bonus_points}{last_session.biggest_bonus.bonus_reason ? ` (${last_session.biggest_bonus.bonus_reason})` : ''}</>}
            </>
          ) : <span style={{ color: '#475569' }}>No recent session</span>}
        </div>
        <div style={{ color: '#94a3b8' }}>
          {hall_of_fame?.winners?.length
            ? `Hall of Fame: ${hall_of_fame.winners.length} period champion${hall_of_fame.winners.length === 1 ? '' : 's'} crowned`
            : 'Hall of Fame: no champions crowned yet'}
        </div>
      </div>
    </div>
  );
}
